
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src2815122270/src/vl-app/pages/patients/profile.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  AutoComplete,
  Button,
  Card,
  Collapse,
  type ColumnsType,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  Link,
  Rule,
  Select,
  Spin,
  Table
} from 'antd';
import { useNotification } from '../../contexts/NotificationContext/NotificationContext';
import { LoadingOutlined } from 'antd/icons';
import * as actionTypes from '@actionTypes';
import AsyncButton from '@components/UI/AsyncButton/AsyncButton';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import * as actions from '@actions';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import useUser from '@vl-core/hooks/useUser';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import useMounted from '@hooks/useMounted';
// import { CardManagementSection } from '@components/Patients/Profile/CardManagement';
// import { CardWarningAlert } from '@components/Patients/Profile/CardManagement/CardWarningAlert';
import { useTranslation } from 'react-i18next';
import useIsNHS from '@hooks/useIsNHS';
import CookieSettings from '@components/CookieConsent/CookieSettings';
import useMetaMisc from '@hooks/useMetaMisc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddDependantModal from '@components/Patients/Profile/AddDependantModal';
import { css } from '@emotion/react';
import Grid from '@components/UI/Grid';
import Flex from '@components/UI/Flex';
import useArticle from 'vl-common/src/hooks/useArticle';
import { sanitiseValueFromAngleBrackets } from 'vl-common/src/utils';
import { DateInput } from 'vl-common/src/components/DateInput';
import { DateTime } from 'luxon';
import 'react-intl-tel-input/dist/main.css';
import { confirmationModal, infoModal } from 'vl-common/src/components/Modals';
import useReactiveColumns from 'vl-common/src/hooks/useReactiveColumns';
import useWidthBreakpoint from 'vl-common/src/hooks/useWidthBreakpoint';
import SurgeryODSFields from '@components/Patients/Registration/items/SurgeryODSFields';
import { GPPracticeFormatted } from '@components/Patients/Registration/utils/useGPPracticesSearch';
import { DocmanConnectivityMessage } from '@components/Patients/Registration/items/GPFieldSet';
import MobileInput from '@components/Patients/MobileInput/MobileInput';
import Header from '@components/Layouts/Header/Header';

type DependantTableColumns = {
  email: string;
  first_name: string;
  updatable: boolean;
  authorised: boolean;
  proxy_status: string;
};

const PageHead = dynamic(() => import('@components/Layouts/PageHead/PageHead'));

const arrayToCommaSeparatedString = (arr) => {
  return String(arr.filter(Boolean).join(', '));
};

const formatAddressFromObject = ({ line_1, line_2, line_3, post_town, county, ...others }) => {
  return arrayToCommaSeparatedString([line_1, line_2, line_3, post_town, county]);
};

export const formatPracticeFromObject = (gpDetails) => {
  const { practice_name, addline1, addline2, addline3, addline4, region, postcode } = gpDetails;
  return arrayToCommaSeparatedString([practice_name, addline1, addline2, addline3, addline4, region, postcode]);
};

const isValidPostcode = (postcode: string) => {
  if (!postcode) return false;

  const postcodeRegex = /^[A-Z]{1,2}\d[\dA-Z]?\s?\d[A-Z]{2}$/i;
  return postcodeRegex.test(postcode);
};

const postcodeRule = () => ({
  validator(_, value) {
    if (value && !isValidPostcode(value)) {
      return Promise.reject(new Error('Please enter a valid postcode'));
    }
    return Promise.resolve();
  }
});

const requiredFieldRule = {
  validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error('This field is required')))
};

const requiredTrimmedFieldRule = {
  validator: (_, value) => (value.trim() ? Promise.resolve() : Promise.reject(new Error('This field is required')))
};

const phoneRule = () => ({
  validator(_, value) {
    if (value && !isValidPhoneNumber(value)) {
      return Promise.reject(new Error('Please enter a valid contact number'));
    }
    return Promise.resolve();
  }
});

const PREF_NOTIFICATION_OPTIONS = [
  { label: 'SMS', value: 'SMS' },
  { label: 'Email', value: 'EMAIL' }
] as const;

type PrefNotification = (typeof PREF_NOTIFICATION_OPTIONS)[number]['value'];

const sanitisePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  return String(phoneNumber.replace(/\s/g, '').replace(/\D/g, '').replace(/^44/, '0'));
};

export const PatientProfile = () => {
  const dispatch = useDispatch();
  const user = useUser();
  const { successNotification, infoNotification } = useNotification();

  const [form] = Form.useForm();
  const [validationForm] = Form.useForm();
  const [editMode, setEditMode] = useState(false);
  const [disabledSave, setDisabledSave] = useState(true);
  const [titlesList, setTitlesList] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [gpIsReadOnly, setGpIsReadOnly] = useState(false);
  // @ts-ignore (no, MULTIPLE_LANGUAGE_SUPPORT does not exist on the object)
  const { MULTIPLE_LANGUAGE_SUPPORT, MFA_SECS, DOCMAN } = useRuntimeConfig();
  const isNHS = useIsNHS();
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
  const [displayAddDependantModal, setDisplayAddDependantModal] = useState(false);
  const [updateProxyRequestDetails, setUpdateProxyRequestDetails] = useState(null);
  const article = useArticle('cookies');
  const readOnlyFieldsContent = useArticle('CONT_PROFILE_READ_ONLY_FIELD');
  const currentBreakPoint = useWidthBreakpoint();

  const telMobileHasErrorsRef = useRef(false);
  const telOtherHasErrorsRef = useRef(false);

  const gpSummaryConsent = Form.useWatch('gp_summary_consent', { form, preserve: true });
  const gpInvestigationConsent = Form.useWatch('gp_investigation_consent', { form, preserve: true });
  const gpAllowedConsent = gpSummaryConsent || gpInvestigationConsent;
  const isGpOdsCodeSelected = Form.useWatch('gp_address_preview', { form, preserve: true });
  const isDocmanActive = Form.useWatch('docman_active', { form, preserve: true });

  const [postBody, setPostBody] = useState({
    user_guid: user.user_guid || '',
    title: user.title || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    pref_name: user.pref_name || '',
    tel_mobile: user.tel_mobile_no || '',
    tel_other: user.tel_other_no || '',
    tel_pref: user.tel_pref_no?.charAt(0).toUpperCase() || '',
    sex: user.sex?.charAt(0) || '',
    dob: user.dob,
    mem_no: user.membership_no || '',
    addline1: user.addline1 || '',
    addline2: user.addline2 || '',
    addline3: user.addline3 || '',
    addline4: user.addline4 || '',
    region: user.region || '',
    postcode: user.postcode || '',
    country: user.country || '',
    gp_email: '',
    gp_addline1: '',
    gp_addline2: '',
    gp_addline3: '',
    gp_addline4: '',
    gp_region: '',
    gp_postcode: '',
    gp_country: '',
    gp_ods_code: '',
    gp_summary_consent: user.gp_summary_consent || null,
    gp_investigation_consent: user.gp_investigation_consent || null,
    nhs_number: user.nhs_number || '',
    gp_excluded: false
  });

  const rawColumns = useMemo<ColumnsType<DependantTableColumns>>(
    () => [
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        responsive: ['md']
      },
      {
        title: 'First Name',
        dataIndex: 'first_name',
        key: 'first_name'
      },
      {
        title: 'Updatable',
        dataIndex: 'agent_updatable',
        key: 'agent_updatable',
        render: (val, data) =>
          val ? (
            <span>
              {t('Yes')}
              <Button
                type="link"
                style={{ padding: '4px 8px ', marginLeft: 8 }}
                onClick={() => {
                  setUpdateProxyRequestDetails(data);
                  setDisplayAddDependantModal(true);
                }}
                data-testid="update-proxy-request"
              >
                {t('(Update)')}
              </Button>
            </span>
          ) : (
            <span>{t('No')}</span>
          )
      },
      {
        title: 'Authorised',
        dataIndex: 'authorised',
        key: 'authorised',
        render: (val) => (val ? 'Yes' : 'No'),
        responsive: ['md']
      },
      {
        title: 'Proxy Status',
        dataIndex: 'proxy_status',
        key: 'proxy_status',
        responsive: ['md']
      }
    ],
    []
  );

  const [visibleColumns, hiddenColumns] = useReactiveColumns(rawColumns, currentBreakPoint);

  const isMounted = useMounted();
  const { /*needs_card_details,*/ proxy_users_enabled } = user;
  const {
    timezones = ['Europe/London', 'Europe/Berlin'],
    // @ts-ignore
    languages = [
      { lang_code: 'en', lang_desc: 'English' },
      { lang_code: 'de', lang_desc: 'German' }
    ]
  } = useMetaMisc();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  const { isLoading: isLoadingProxyRequests, data: proxyRequests = [] } = useQuery({
    queryKey: ['proxy-requests'],
    queryFn: async () => {
      const { proxy_requests = [] } = await dispatch(actions.getProxyRequests());
      return proxy_requests;
    },
    enabled: !!proxy_users_enabled
  });

  useEffect(() => {
    if (editMode) {
      form.validateFields(['gp_address_preview']);
    }

    if (editMode && !gpAllowedConsent) {
      form.setFields([
        {
          name: 'gp_address_preview',
          errors: []
        }
      ]);
    }

    if (editMode && isGpOdsCodeSelected && gpAllowedConsent) {
      form.validateFields([FIELD_NAMES.nhs_number]);
    }
  }, [gpAllowedConsent, editMode, form, isGpOdsCodeSelected]);

  const FIELD_NAMES = {
    title: 'title',
    first_name: 'first_name',
    last_name: 'last_name',
    pref_name: 'pref_name',
    dob: 'dob',
    tel_mobile: 'tel_mobile',
    tel_other: 'tel_other',
    pref_notification: 'pref_notification',
    sex: 'sex',
    mem_no: 'mem_no',
    addline1: 'addline1',
    addline2: 'addline2',
    addline3: 'addline3',
    addline4: 'addline4',
    region: 'region',
    postcode: 'postcode',
    country: 'country',
    language: 'language',
    timezone: 'timezone',
    gp_summary_consent: 'gp_summary_consent',
    gp_investigation_consent: 'gp_investigation_consent',
    nhs_number: 'nhs_number'
  };

  const initialValues = {
    [FIELD_NAMES.title]: user.title,
    [FIELD_NAMES.first_name]: user.first_name,
    [FIELD_NAMES.last_name]: user.last_name,
    [FIELD_NAMES.pref_name]: user.pref_name,
    [FIELD_NAMES.dob]: user.dob ? user.dob.toISOString().split('T')[0] : '',
    [FIELD_NAMES.tel_mobile]: `${user.tel_mobile_no}`,
    [FIELD_NAMES.tel_other]: user?.tel_other_no ? `${user.tel_other_no}` : '',
    [FIELD_NAMES.pref_notification]: user.pref_notification,
    [FIELD_NAMES.language]: '',
    [FIELD_NAMES.timezone]: user?.timezone,

    [FIELD_NAMES.addline1]: user.addline1 || '',
    [FIELD_NAMES.addline2]: user.addline2 || '',
    [FIELD_NAMES.addline3]: user.addline3 || '',
    [FIELD_NAMES.addline4]: user.addline4 || '',
    [FIELD_NAMES.region]: user.region || '',
    [FIELD_NAMES.postcode]: user.postcode || '',
    [FIELD_NAMES.country]: user.country || '',

    [FIELD_NAMES.gp_summary_consent]: user.gp_summary_consent,
    [FIELD_NAMES.gp_investigation_consent]: user.gp_investigation_consent,
    [FIELD_NAMES.mem_no]: user.membership_no || '',
    [FIELD_NAMES.nhs_number]: user.nhs_number || ''
  };

  const fetchData = useCallback(async () => {
    if (!user?.user_guid || !isMounted()) return;

    const result = await dispatch(actions.usersMeta());
    if (result.meta) {
      setTitlesList(result.meta[0].titles);
    }

    const gpDetails = await dispatch(actions.getGpDetails(user.user_guid));

    if (gpDetails) {
      setGpIsReadOnly(gpDetails.ro_fields?.includes('gp_ods_code'));
      form.setFieldsValue({
        gp_address_preview: gpDetails?.gp_ods_code ? formatPracticeFromObject(gpDetails) : '',
        docman_active: gpDetails.docman_active
      });
      setPostBody((postBody) => ({
        ...postBody,
        gp_email: gpDetails.email || '',
        gp_addline1: gpDetails.addline1 || '',
        gp_addline2: gpDetails.addline2 || '',
        gp_addline3: gpDetails.addline3 || '',
        gp_addline4: gpDetails.addline4 || '',
        gp_region: gpDetails.region || '',
        gp_postcode: gpDetails.postcode || '',
        gp_country: gpDetails.country || '',
        gp_ods_code: gpDetails.gp_ods_code || ''
      }));
    }
  }, [dispatch, isMounted, user.user_guid]);

  const resetFields = () => {
    // Ensure all fields reset properly and validation cleared
    form.resetFields();

    form.setFieldsValue({
      address_lookup: '',
      gp_address_lookup: '',
      gp_address_preview: ''
    });
    fetchData();
  };

  useEffect(() => {
    fetchData().then();
  }, [fetchData]);

  const searchPostcode = async (pc) => {
    if (!isValidPostcode(pc)) return;

    const addresses = await dispatch(actions.fetchAddresses(pc), false);
    if (addresses.result) {
      setAddressList(addresses.result);
    }
  };

  const mobileNumberIsValidated = () => {
    const currentNumber = form.getFieldValue(FIELD_NAMES.tel_mobile);
    const previousNumber = user?.tel_mobile_no;

    const numberIsUnchanged = sanitisePhoneNumber(currentNumber) === sanitisePhoneNumber(previousNumber);

    return numberIsUnchanged && !!user?.tel_validated;
  };

  const persistSmsPreferenceIfValidationNeeded = async (newPreference: PrefNotification) => {
    if (newPreference !== 'SMS') return false;
    if (mobileNumberIsValidated()) return false;

    return verifySmsCodeAndPersistPreference();
  };

  const saveDetails = async () => {
    const newPostBody = {
      ...postBody,
      title: form.getFieldValue(FIELD_NAMES.title),
      first_name: form.getFieldValue(FIELD_NAMES.first_name),
      last_name: form.getFieldValue(FIELD_NAMES.last_name),
      pref_name: form.getFieldValue(FIELD_NAMES.pref_name),
      tel_mobile: form.getFieldValue(FIELD_NAMES.tel_mobile),
      tel_other: form.getFieldValue(FIELD_NAMES.tel_other) || '',
      dob: form.getFieldValue(FIELD_NAMES.dob),
      lang_code: form.getFieldValue(FIELD_NAMES.language),
      timezone: form.getFieldValue(FIELD_NAMES.timezone),

      addline1: form.getFieldValue(FIELD_NAMES.addline1),
      addline2: form.getFieldValue(FIELD_NAMES.addline2),
      addline3: form.getFieldValue(FIELD_NAMES.addline3),
      addline4: form.getFieldValue(FIELD_NAMES.addline4),
      region: form.getFieldValue(FIELD_NAMES.region),
      postcode: form.getFieldValue(FIELD_NAMES.postcode),
      country: form.getFieldValue(FIELD_NAMES.country),
      gp_summary_consent: form.getFieldValue(FIELD_NAMES.gp_summary_consent),
      gp_investigation_consent: form.getFieldValue(FIELD_NAMES.gp_investigation_consent),
      nhs_number: form.getFieldValue(FIELD_NAMES.nhs_number)
    };

    if (gpIsReadOnly) newPostBody.gp_excluded = true;

    const modal = infoModal({
      title: t('Updating profile'),
      content: t('Please wait...'),
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } }
    });

    const res = await dispatch(actions.updateDetails(newPostBody));

    if (res.type === actionTypes.UPDATE_DETAILS_FAIL && res.error) {
      modal.destroy();
    } else if (res.type === actionTypes.UPDATE_DETAILS_SUCCESS && res.payload) {
      setPostBody(newPostBody);
      setEditMode(false);

      const smsPreferenceAlreadyPersisted = await persistSmsPreferenceIfValidationNeeded(
        form.getFieldValue(FIELD_NAMES.pref_notification)
      );
      if (!smsPreferenceAlreadyPersisted) {
        await changePref(form.getFieldValue(FIELD_NAMES.pref_notification));
      }
      modal.destroy();

      successNotification({
        message: t('Profile updated'),
        description: t('Your profile has been updated successfully')
      });

      const gpDetails = await dispatch(actions.getGpDetails(user.user_guid));

      DOCMAN &&
        gpDetails?.docman_invalid_gp &&
        infoModal({
          title: t('Your GP details are still invalid'),
          content: t(
            "You've entered the details of a GP surgery that previously told us you're not on their records. Please be aware that we cannot share your documents with them."
          ),
          okText: t('OK'),
          centered: true
        });

      await dispatch(actions.userFetch());
    }
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setDisabledSave(hasErrors);
  };

  const handlePhoneChange = (fieldName: string, errorRef: { current: boolean }, fullNumber: string) => {
    if (errorRef.current) {
      return;
    }
    form.setFieldValue(fieldName, fullNumber);
  };

  const handlePhoneBlur = async (fieldName: string, errorRef: { current: boolean }) => {
    if (!editMode) return;

    try {
      await form.validateFields([fieldName]);
      errorRef.current = false;
    } catch (errorInfo: any) {
      errorRef.current = true;
      form.setFields(errorInfo.errorFields);

      setTimeout(() => {
        errorRef.current = false;
      }, 200);
    }

    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setDisabledSave(hasErrors);
  };

  const onClickAddress = async (addr) => {
    setAddressList([]);
    setPostBody({
      ...postBody,
      addline1: addr.line_1,
      addline2: addr.line_2,
      addline3: addr.line_3,
      addline4: addr.post_town,
      region: addr.county,
      postcode: addr.postcode,
      country: user.country
    });
    form.setFieldsValue({
      address_lookup: addr.postcode.toUpperCase(),
      [FIELD_NAMES.postcode]: addr.postcode.toUpperCase(),
      [FIELD_NAMES.addline1]: addr.line_1,
      [FIELD_NAMES.addline2]: addr.line_2,
      [FIELD_NAMES.addline3]: addr.line_3,
      [FIELD_NAMES.addline4]: addr.post_town,
      [FIELD_NAMES.region]: addr.county
    });
  };

  const handleGPSelect = useCallback(
    (selectedPractice: GPPracticeFormatted) => {
      setPostBody((prev) => ({
        ...prev,
        gp_ods_code: selectedPractice.ods_code,
        gp_postcode: selectedPractice.gp_postcode
      }));
    },
    [setPostBody]
  );

  const changePref = async (prefer: PrefNotification) => {
    const pref = {
      user_guid: user.user_guid || '',
      method: prefer,
      preference: true
    };
    await dispatch(actions.updateNotificationPref(pref));
    await dispatch(actions.userFetch());
  };

  const submitCode = async (type, code) => {
    const sendCode = {
      user_guid: user.user_guid || '',
      method: type,
      code
    };

    const sendToastOnFailure = false;
    const response = await dispatch(actions.updateNotificationPref(sendCode), sendToastOnFailure);
    if (response.status === 406 || response.status === 400) {
      return false;
    }

    await dispatch(
      actions.updateNotificationPref({
        user_guid: user.user_guid || '',
        method: form.getFieldValue(FIELD_NAMES.pref_notification),
        preference: true
      })
    );

    await dispatch(actions.userFetch());

    return true;
  };

  const resendCode = async (type) => {
    const sendCode = {
      user_guid: user.user_guid || '',
      method: type,
      resend: true
    };
    await dispatch(actions.resendCode(sendCode));

    successNotification({
      message: `Validation code sent via ${type}`
    });
  };

  const acceptCookies = (cookies) => {
    if (cookies) {
      localStorage.setItem('cookie-consent', cookies.map((cookie) => cookie.substring(0, 3)).join(' '));
      setCookieSettingsOpen(false);
    } else {
      localStorage.setItem('cookie-consent', 'all');
      setCookieSettingsOpen(false);
    }
  };

  const cancelChanges = () => {
    confirmationModal({
      title: 'Are you sure you want to cancel?',
      content: 'Any changes you have made will be lost.',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        resetFields();
        setEditMode(false);
      },
      autoFocusButton: 'cancel',
      closable: true,
      okButtonProps: { type: 'default' },
      cancelButtonProps: { type: 'primary' }
    });
  };

  const goToProviderSettings = async () => {
    window.open(user.provider_settings_url);

    const res = await dispatch(actions.userLogout(queryClient));
    if (res.type === actionTypes.AUTH_LOGOUT_SUCCESS) {
      await router.push('/login');
    }
  };

  const redirectToProviderSettings = () => {
    const modalContent = readOnlyFieldsContent ? (
      <div className="read-only-fields-content" dangerouslySetInnerHTML={{ __html: readOnlyFieldsContent.body }} />
    ) : (
      <>{t("You've logged into HBSUK using an external account. To update your detail")}</>
    );

    if (user.ident_prov_code === 'AXAPAT' || user.ident_prov_code === 'NHSLOGIN') {
      confirmationModal({
        title: 'Edit profile details',
        content: modalContent,
        okText: 'OK',
        onOk: () => {
          setEditMode(true);
        }
      });
    }
  };

  const handleEditModeStateChange: React.MouseEventHandler<HTMLButtonElement> = () => {
    if (user.auth_type === 'SSO' && user.ident_prov_code === 'NHSLOGIN' && !editMode) {
      redirectToProviderSettings();
      return;
    }

    // AXA SSO allows some details to be edited on the page
    if (user.auth_type === 'SSO' && user.ident_prov_code === 'AXAPAT' && !editMode && user.ro_field_msg) {
      redirectToProviderSettings();
      return;
    }

    if (!editMode) {
      setEditMode(true);
      return;
    }

    saveDetails();
  };

  const verifySmsCodeAndPersistPreference = async (): Promise<boolean> => {
    try {
      await dispatch(
        actions.resendCode({
          user_guid: user.user_guid || '',
          method: 'SMS',
          preference: true
        }),
        false
      );
    } catch (e) {
      console.log(e);
    }

    const modal = new Promise<boolean>((resolve) => {
      confirmationModal({
        title: 'SMS validation',
        content: (
          <Form form={validationForm} layout="vertical" id="validate-form" validateTrigger="onSubmit">
            <p>
              {t('To receive SMS notifications, please validate your phone number. We have sent a')}{' '}
              <code>{form.getFieldValue(FIELD_NAMES.tel_mobile)}</code>.
            </p>

            <Form.Item
              name="code"
              label={t('Enter code')}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '' || value.length !== 6) {
                      return Promise.reject('Please enter a valid code');
                    }

                    if (!/^\d+$/.test(value)) {
                      return Promise.reject('Only numbers are allowed');
                    }

                    if (value.startsWith(0)) {
                      return Promise.reject('Code should not start with a zero');
                    }

                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input size="large" placeholder="123456" />
            </Form.Item>

            <Collapse>
              <Collapse.Panel key="1" header="Didn't receive a code?">
                <p>{t("If your code hasn't arrived or has expired, you can request a new one.")}</p>
                <AsyncButton
                  onClick={() => resendCode('SMS')}
                  disableInitially
                  delay={Number(MFA_SECS) || 60}
                  data-testid="resend-code"
                >
                  <Flex>
                    <AsyncButton.Button data-testid="resend-code-button">{t('Resend code')}</AsyncButton.Button>
                    <AsyncButton.TimeLeft type="SMS" data-testid="resend-code-time-left" />
                  </Flex>
                </AsyncButton>
              </Collapse.Panel>
            </Collapse>
          </Form>
        ),
        okText: 'Submit',
        onOk: async () => {
          await validationForm.validateFields();
          try {
            const isCorrectCode = await submitCode('SMS', validationForm.getFieldValue('code'));

            return new Promise<void>((resolveValidationCheck, reject) => {
              if (isCorrectCode) {
                validationForm.resetFields();
                resolveValidationCheck();
                resolve(true);
              } else {
                validationForm.setFields([
                  {
                    name: 'code',
                    value: '',
                    errors: ['Incorrect validation code']
                  }
                ]);

                reject();
              }
            });
          } catch (e) {
            return new Promise((resolve, reject) => {
              validationForm.setFields([
                {
                  name: 'code',
                  value: '',
                  errors: [e.message]
                }
              ]);

              reject();
            });
          }
        },
        onCancel: () => {
          form.setFieldsValue({
            [FIELD_NAMES.pref_notification]: 'EMAIL'
          });
          infoNotification({
            message: 'Contact preference set to email'
          });
          resolve(false);
        }
      });
    });

    return modal;
  };

  const dateOfBirthRules: Rule[] = [
    {
      validator: (_, value) => {
        try {
          const parsedDate = DateTime.fromFormat(value, 'yyyy-M-d');

          if (parsedDate.diffNow().milliseconds > 0) {
            return Promise.reject(new Error('Date of birth must be in the past'));
          }

          if (parsedDate.diffNow('years').years > -18) {
            return Promise.reject(new Error('You must be 18 or over to use our service.'));
          }

          if (parsedDate.diffNow('years').years < -120) {
            return Promise.reject(new Error('Date of birth must be less than 120 years ago.'));
          }

          return Promise.resolve();
        } catch {
          return Promise.reject(new Error('Date of birth must be a real date.'));
        }
      }
    }
  ];

  const enhancedDisabledProps = {
    disabled: true,
    css: css`
      &,
      .ant-picker-input > input[disabled],
      .ant-select-selector,
      .ant-input {
        cursor: default !important;
        color: #000 !important;
      }
    `
  };

  const axaSSOUser = user.ident_prov_code === 'AXAPAT' && user.auth_type === 'SSO';

  const handleFieldDisabling = (fieldName: string) => {
    if (editMode) {
      return user.ro_fields.includes(fieldName) ? { ...enhancedDisabledProps } : {};
    }

    return { ...enhancedDisabledProps };
  };

  return (
    <div className="page-container">
      <Header pageType="patients" pageTitle="Profile" />

      <section className="section-layout patient" data-testid="patient-profile-component">
        <main className="container" id="page-content">
          <PageHead backText="Profile" backArrow={false} backUrl="/patients" buttonGroup={null} />

          {/* <CardWarningAlert needs_card_details={needs_card_details} /> */}

          <Card
            title="Personal Details"
            css={css`
              max-width: 1024px;
              margin: 1rem auto;
            `}
            extra={
              <Flex>
                {editMode && (user.ident_prov_code === 'AXAPAT' || user.ident_prov_code === 'NHSLOGIN') && (
                  <Link
                    href={user.provider_settings_url ?? ''}
                    target="_blank"
                    onClick={goToProviderSettings}
                    css={css`
                      margin: 5px 5px 0 0;
                    `}
                  >
                    {user.ident_prov_code === 'AXAPAT' ? (
                      <>{t('Edit your AXA Health details here')}</>
                    ) : (
                      <>{t('Edit your NHS login details here')}</>
                    )}
                  </Link>
                )}
                {editMode && (
                  <Button onClick={cancelChanges} data-testid="cancel">
                    {t('Cancel')}
                  </Button>
                )}

                <Button
                  onClick={handleEditModeStateChange}
                  type="primary"
                  data-testid="edit-details-button"
                  disabled={editMode && disabledSave}
                >
                  {editMode ? 'Save' : 'Edit'}
                </Button>
              </Flex>
            }
          >
            <p>{t('NHSLoginProfilePage')}</p>

            <p>
              {isNHS ? t('PatientId') : t('Membership Number')}: {user?.membership_no}
            </p>

            <Form {...{ form, initialValues }} onFieldsChange={handleFormChange} layout="vertical">
              <Grid columns={2}>
                <Form.Item label={t('Title')} name={FIELD_NAMES.title}>
                  <Select
                    options={titlesList.map(({ title }) => ({ value: title, label: title }))}
                    {...handleFieldDisabling(FIELD_NAMES.title)}
                    data-testid="title-select"
                  />
                </Form.Item>

                <Form.Item
                  label={t('First Name')}
                  name={FIELD_NAMES.first_name}
                  required
                  rules={[requiredTrimmedFieldRule]}
                >
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.first_name)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.first_name, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>
              </Grid>

              <Grid columns={2}>
                <Form.Item
                  label={t('Last Name')}
                  name={FIELD_NAMES.last_name}
                  required
                  rules={[requiredTrimmedFieldRule]}
                >
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.last_name)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.last_name, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>
                <Form.Item label={t('Preferred Name')} name={FIELD_NAMES.pref_name}>
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.pref_name)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.pref_name, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>
              </Grid>

              <Form.Item
                name={FIELD_NAMES.dob}
                getValueFromEvent={(value) => DateTime.fromFormat(value, 'yyyy-M-d').toISODate()}
                required
                rules={dateOfBirthRules}
              >
                <DateInput label={t('DateOfBirth')} required {...handleFieldDisabling(FIELD_NAMES.dob)} />
              </Form.Item>
              <Divider />
              <Grid columns={2}>
                <Form.Item
                  label={t('Preferred Contact Number')}
                  name={FIELD_NAMES.tel_mobile}
                  required
                  rules={[
                    phoneRule,
                    requiredFieldRule,
                    {
                      validator: (_, value) => {
                        if (value && !mobileNumberIsValidated()) {
                          return Promise.reject(new Error('This number has not been validated.'));
                        }

                        return Promise.resolve();
                      },
                      warningOnly: true
                    }
                  ]}
                >
                  {/* Mounting and initialising the component properly via conditional rendering
                   to prevent display problems e.g. duplicate country codes showing on first load */}
                  {(form.getFieldValue(FIELD_NAMES.tel_mobile) || editMode) && (
                    <MobileInput
                      inputId="tel_mobile"
                      mobile={form.getFieldValue(FIELD_NAMES.tel_mobile)}
                      setMobile={(fullNumber) =>
                        handlePhoneChange(FIELD_NAMES.tel_mobile, telMobileHasErrorsRef, fullNumber)
                      }
                      onBlur={() => handlePhoneBlur(FIELD_NAMES.tel_mobile, telMobileHasErrorsRef)}
                      disabled={!editMode || user.ro_fields.includes(FIELD_NAMES.tel_mobile)}
                      location="profile"
                      separateTelCode
                    />
                  )}
                </Form.Item>

                {(form.getFieldValue(FIELD_NAMES.tel_mobile) || editMode) && (
                  <Form.Item label={t('Alternative Contact Number')} name={FIELD_NAMES.tel_other} rules={[phoneRule]}>
                    <MobileInput
                      inputId="tel_other"
                      mobile={form.getFieldValue(FIELD_NAMES.tel_other)}
                      setMobile={(fullNumber) =>
                        handlePhoneChange(FIELD_NAMES.tel_other, telOtherHasErrorsRef, fullNumber)
                      }
                      onBlur={() => handlePhoneBlur(FIELD_NAMES.tel_other, telOtherHasErrorsRef)}
                      disabled={!editMode}
                      location="profile"
                      separateTelCode
                    />
                  </Form.Item>
                )}

                {MULTIPLE_LANGUAGE_SUPPORT && (
                  <Form.Item label={t('Language')} name={FIELD_NAMES.language}>
                    <Select
                      options={languages.map(({ lang_code, lang_desc }) => ({ value: lang_code, label: lang_desc }))}
                      {...handleFieldDisabling(FIELD_NAMES.language)}
                      data-testid="language-select"
                    />
                  </Form.Item>
                )}

                <Form.Item label={t('Timezone')} name={FIELD_NAMES.timezone}>
                  <Select
                    options={timezones.map((tz) => ({ value: tz, label: tz }))}
                    {...handleFieldDisabling(FIELD_NAMES.timezone)}
                    data-testid="timezone-select"
                  />
                </Form.Item>

                <Form.Item label={t('Preferred Contact Method')} name={FIELD_NAMES.pref_notification}>
                  <Select
                    options={[...PREF_NOTIFICATION_OPTIONS]}
                    {...handleFieldDisabling(FIELD_NAMES.pref_notification)}
                    data-testid="pref-notification-select"
                  />
                </Form.Item>
              </Grid>

              <Divider />

              <Form.Item
                label={t('Lookup address by postcode')}
                name="address_lookup"
                extra="Enter your full postcode to search for your address"
                initialValue={form.getFieldValue(FIELD_NAMES.postcode)}
                rules={[postcodeRule]}
                hidden={!editMode || axaSSOUser}
              >
                <AutoComplete
                  options={addressList.map((address, index) => ({
                    value: index,
                    label: formatAddressFromObject(address)
                  }))}
                  onSearch={(v) => searchPostcode(v.toUpperCase())}
                  onSelect={(i) => onClickAddress(addressList[i])}
                  css={css`
                    width: 100%;

                    input {
                      text-transform: uppercase;
                    }
                  `}
                />
              </Form.Item>
              <Grid columns={3}>
                <Form.Item
                  label={t('Address Line 1')}
                  name={FIELD_NAMES.addline1}
                  required
                  rules={[requiredTrimmedFieldRule]}
                >
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.addline1)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.addline1, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>

                <Form.Item label={t('Address Line 2')} name={FIELD_NAMES.addline2}>
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.addline2)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.addline2, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>

                <Form.Item label={t('Address Line 3')} name={FIELD_NAMES.addline3}>
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.addline3)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.addline3, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>

                <Form.Item label={t('Town/City')} name={FIELD_NAMES.addline4}>
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.addline4)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.addline4, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>

                <Form.Item label={t('County')} name={FIELD_NAMES.region}>
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.region)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.region, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label={t('Postcode')}
                  name={FIELD_NAMES.postcode}
                  required
                  rules={[requiredTrimmedFieldRule]}
                >
                  <Input
                    {...handleFieldDisabling(FIELD_NAMES.postcode)}
                    onChange={(e) => {
                      form.setFieldValue(FIELD_NAMES.postcode, sanitiseValueFromAngleBrackets(e.target.value));
                    }}
                  />
                </Form.Item>
              </Grid>
              <Divider />
              <SurgeryODSFields
                form={form}
                FormItemComponent={Form.Item}
                initialSearchValue={form.getFieldValue(FIELD_NAMES.postcode)}
                hidden={!editMode}
                isConsentGiven={gpAllowedConsent}
                onSelect={handleGPSelect}
                hasSavedGPWithDocmanNotActive={!!isGpOdsCodeSelected && !isDocmanActive}
              />

              {!!isGpOdsCodeSelected && <DocmanConnectivityMessage gpAcceptsDocman={isDocmanActive} />}

              {/* <Divider />

              <CardManagementSection user={user} enhancedDisabledProps={enhancedDisabledProps} /> */}

              <Divider />

              <Form.Item label={t('Cookie settings')} name="cookie settings">
                <Button onClick={() => setCookieSettingsOpen(!cookieSettingsOpen)} data-testid="cookie-settings-button">
                  {t('Edit cookie settings')}
                </Button>
              </Form.Item>

              {!!proxy_users_enabled && (
                <Form.Item label={t('Proxy Accounts (Dependants)')} name="dependants">
                  <Button
                    onClick={() => {
                      setDisplayAddDependantModal(true);
                    }}
                    data-testid="add-dependant"
                    css={css`
                      margin: 0 0 1rem;
                    `}
                  >
                    {t('Add dependant')}
                  </Button>

                  <Table
                    rowKey={(account) => account?.email}
                    columns={visibleColumns}
                    dataSource={proxyRequests}
                    pagination={false}
                    size="small"
                    locale={{
                      emptyText: isLoadingProxyRequests ? (
                        <div style={{ height: '32px' }}>
                          <Spin indicator={<LoadingOutlined style={{ fontSize: 26 }} spin />} />
                        </div>
                      ) : (
                        <Empty description={t('No proxies found')} />
                      )
                    }}
                    expandable={{
                      expandedRowRender: (record) => (
                        <Descriptions>
                          {hiddenColumns.map((item) => {
                            if (!item.dataIndex) return null;

                            const dataIndex = String(item.dataIndex);
                            if (!(dataIndex in record)) return null;

                            const value = record[dataIndex as keyof DependantTableColumns];

                            return (
                              <Descriptions.Item key={item.dataIndex.toString()} label={item.title}>
                                {typeof value === 'boolean' ? <>{value ? 'Yes' : 'No'}</> : value}
                              </Descriptions.Item>
                            );
                          })}
                        </Descriptions>
                      ),
                      rowExpandable: () => hiddenColumns.length > 0
                    }}
                  />
                </Form.Item>
              )}
            </Form>
          </Card>
        </main>
      </section>

      {!!proxy_users_enabled && (
        <AddDependantModal
          open={displayAddDependantModal}
          onCancel={() => setDisplayAddDependantModal(false)}
          user={user}
          updateProxyRequestDetails={updateProxyRequestDetails}
          setUpdateProxyRequestDetails={setUpdateProxyRequestDetails}
        />
      )}

      {cookieSettingsOpen && (
        <CookieSettings
          setSettingsOpen={setCookieSettingsOpen}
          acceptCookies={acceptCookies}
          setBannerOpen={() => {}}
          article={article}
        />
      )}
    </div>
  );
};

const WrappedPatientProfile = () => {
  const { t } = useTranslation('common');
  const initialised = useInitialisedStore();

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  return <PatientProfile />;
};

export default WrappedPatientProfile;
export const Route = createFileRoute('/patients/profile')({ component: WrappedPatientProfile });
